import { useCallback, useEffect } from 'react';

import {
  DUBBING_ERROR_STATUSES,
  PROJECT_TASK_TYPE,
  ProjectTaskType,
  useGetProject,
  useProjectLoadingTaskStore,
} from '@/entities/project';

import { usePrevious } from '@/shared/hooks';
import { useAppDispatch, useAppSelector } from '@/shared/hooks-tmp/reduxToolkitHooks';
import { showErrorAlert } from '@/shared/lib/showErrorAlert';
import { setIsVoiceSuggesting } from '@/shared/store-tmp/editor/EditorSlice';
import { getAllSegments } from '@/shared/store-tmp/editor/thunks/getAllSegments';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { Project } from '@/shared/types/Project';
import { Status } from '@/shared/types/Status';

import { useProjectState } from './useProjectState';
import { useProjectUpdating } from './useProjectUpdating';

const INITIAL_STATUS: {
  [key in Status]?: ProjectTaskType;
} = {
  [Status.mergingStarted]: PROJECT_TASK_TYPE.VideoGenerating,
  [Status.voiceoverDone]: PROJECT_TASK_TYPE.VideoGenerating,
  [Status.voiceoverStarted]: PROJECT_TASK_TYPE.VideoGenerating,
  [Status.voiceUpdateStarted]: PROJECT_TASK_TYPE.VoiceUpdate,
};

export const useProjectLoadingStateUpdating = () => {
  const dispatch = useAppDispatch();

  const { data: project } = useGetProject();
  const isVoiceSuggesting = useAppSelector((state) => state.editor.isVoiceSuggesting);

  const { actionBeforePolling, projectLoadingTask, resetProjectLoadingState, updateProjectLoadingState } =
    useProjectLoadingTaskStore();
  const prevLoadingTask = usePrevious(projectLoadingTask);

  const {
    areNotAllSpeakersHaveVoices,
    isAutogeneratingProject,
    isProjectProcessed,
    projectFinalProcessingStatuses,
    projectTaskType,
  } = useProjectState();

  const processingTaskType = isAutogeneratingProject || !isProjectProcessed ? projectTaskType : null;

  const loadingTaskBasedOnProjectStatus =
    processingTaskType ||
    (project && INITIAL_STATUS[project.status]) ||
    (areNotAllSpeakersHaveVoices ? PROJECT_TASK_TYPE.VoiceUpdate : null);

  const isVideoGenerating = projectLoadingTask === PROJECT_TASK_TYPE.VideoGenerating;
  const isVoiceUpdating = projectLoadingTask === PROJECT_TASK_TYPE.VoiceUpdate;

  const onProjectUpdated = useCallback(
    async (updatingProject: Project) => {
      try {
        if (isVideoGenerating) {
          MixpanelEvents.videoIsGenerated();
          // After the video is generated, segments with the status "updated" should be set to "done"
          await dispatch(
            getAllSegments({ projectId: updatingProject.id, shouldRecalculateOverlappedTimestamps: true }),
          );
        }
      } catch (e) {
        showErrorAlert(e);
      }
      if (isVoiceUpdating) {
        dispatch(setIsVoiceSuggesting(false));
      }
    },
    [isVideoGenerating, isVoiceUpdating, dispatch],
  );

  const voiceUpdatingFinalStatuses = isVoiceUpdating ? [Status.voiceUpdateDone, Status.voiceUpdateFailed] : null;
  const videoGeneratingFinalStatuses = isVideoGenerating ? [...DUBBING_ERROR_STATUSES, Status.mergingDone] : null;

  const finalStatuses = projectLoadingTask
    ? voiceUpdatingFinalStatuses || videoGeneratingFinalStatuses || projectFinalProcessingStatuses
    : [];
  const { startProjectUpdating, fetchAndUpdateProject } = useProjectUpdating({
    finalStatuses,
    project,
    onProjectUpdated,
  });

  const setLoadingTaskBasedOnProjectStatus = useCallback(() => {
    if (loadingTaskBasedOnProjectStatus === PROJECT_TASK_TYPE.VoiceUpdate) {
      dispatch(setIsVoiceSuggesting(true));
    }

    updateProjectLoadingState({ projectLoadingTask: loadingTaskBasedOnProjectStatus });

    if (loadingTaskBasedOnProjectStatus) {
      startProjectUpdating();
    }
  }, [loadingTaskBasedOnProjectStatus, dispatch, updateProjectLoadingState, startProjectUpdating]);

  const isProjectFetched = Boolean(project);

  useEffect(() => {
    if (isProjectFetched) {
      setLoadingTaskBasedOnProjectStatus();
    }

    return () => {
      resetProjectLoadingState();
    };
  }, [isProjectFetched, resetProjectLoadingState, setLoadingTaskBasedOnProjectStatus]);

  const startPolling = useCallback(async () => {
    try {
      if (actionBeforePolling) {
        updateProjectLoadingState({ actionBeforePolling: null, projectLoadingTask });
        await actionBeforePolling();
      }
      await fetchAndUpdateProject();
      startProjectUpdating();
    } catch (e) {
      console.error(e);
      dispatch(setIsVoiceSuggesting(false));
      updateProjectLoadingState({ projectLoadingTask: null });
    }
  }, [
    startProjectUpdating,
    fetchAndUpdateProject,
    actionBeforePolling,
    dispatch,
    projectLoadingTask,
    updateProjectLoadingState,
  ]);

  useEffect(() => {
    if (
      projectLoadingTask === PROJECT_TASK_TYPE.VideoGenerating &&
      prevLoadingTask !== PROJECT_TASK_TYPE.VideoGenerating
    ) {
      void startPolling();
    }
  }, [projectLoadingTask, prevLoadingTask, startPolling]);

  useEffect(() => {
    if (isVoiceSuggesting && !projectLoadingTask) {
      updateProjectLoadingState({ projectLoadingTask: PROJECT_TASK_TYPE.VoiceUpdate });
      void startPolling();
      return;
    }

    if (!isVoiceSuggesting && projectLoadingTask === PROJECT_TASK_TYPE.VoiceUpdate) {
      updateProjectLoadingState({ projectLoadingTask: null });
    }
  }, [isVoiceSuggesting, projectLoadingTask, startPolling, resetProjectLoadingState, updateProjectLoadingState]);
};
